import React, { useState } from "react";
import WizardLayout from "../../../../components/common/formkit/WizardLayout";
import { useUnsavedChangesGuard, useExitWizard } from "../../../../components/common/formkit/formHelpers";
import Step1New from "./step1New";
import Step2Details from "./Step2Details";
import Step3Medias from "./Step3Medias";
import Step4Features from "./Step4Features";
import Step5Preview from "./Step5Preview";

const STEP_FORM_ID = "property-step-form";

const ALL_STEPS = [
  { key: "basic", title: "Basic Details", description: "Purpose & type" },
  { key: "details", title: "Pricing & Location", description: "Budget, place, description" },
  { key: "media", title: "Media", description: "Photos, videos, documents" },
  { key: "features", title: "Features & Amenities", description: "Rooms and facilities" },
  { key: "review", title: "Review & Submit", description: "Confirm and publish" },
];

/**
 * Shared multi-step property form used by Create Property (My Properties / Shared Properties)
 * and Edit Property. The parent owns `form` and the submit call.
 */
const PropertyWizard = ({
  mode = "create",
  form,
  setForm,
  onSubmit,
  isSubmitting = false,
  loading = false,
  isDirty = false,
}) => {
  const isEdit = mode === "edit";
  const [currentKey, setCurrentKey] = useState("basic");
  const [reachedKeys, setReachedKeys] = useState([]);

  useUnsavedChangesGuard(isDirty && !isSubmitting);
  const exitWizard = useExitWizard();

  // Same as mobile: the media step is only shown when creating a Sell / Rent-out listing
  const hasMediaStep = isEdit || ["Sell", "Rent-out"].includes(form.propertyPurposeName);
  const steps = ALL_STEPS.filter((s) => s.key !== "media" || hasMediaStep);

  const goTo = (key) => setCurrentKey(key);

  const nextStep = () => {
    const index = steps.findIndex((s) => s.key === currentKey);
    setReachedKeys((prev) => (prev.includes(currentKey) ? prev : [...prev, currentKey]));
    if (index < steps.length - 1) setCurrentKey(steps[index + 1].key);
  };

  const prevStep = () => {
    const index = steps.findIndex((s) => s.key === currentKey);
    if (index > 0) setCurrentKey(steps[index - 1].key);
  };

  const renderStep = () => {
    const shared = { form, setForm, nextStep, formId: STEP_FORM_ID };
    switch (currentKey) {
      case "basic":
        return <Step1New {...shared} />;
      case "details":
        return <Step2Details {...shared} />;
      case "media":
        return <Step3Medias {...shared} />;
      case "features":
        return <Step4Features {...shared} />;
      case "review":
        return <Step5Preview form={form} goTo={goTo} hasMediaStep={hasMediaStep} />;
      default:
        return null;
    }
  };

  const isLast = currentKey === "review";

  return (
    <WizardLayout
      title={isEdit ? "Edit Property" : "Create New Property"}
      subtitle={
        isEdit
          ? "Update the details of your listing."
          : "Fill in the property details to publish your listing across the marketplace."
      }
      steps={steps}
      currentKey={currentKey}
      reachedKeys={reachedKeys}
      onStepClick={goTo}
      onBack={prevStep}
      onExit={exitWizard}
      onNext={onSubmit}
      stepFormId={STEP_FORM_ID}
      isLast={isLast}
      nextLabel={isLast ? (isEdit ? "Save Changes" : "Publish Property") : "Next"}
      submitting={isSubmitting}
      loading={loading}
    >
      {renderStep()}
    </WizardLayout>
  );
};

export default PropertyWizard;
